import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './PageStyles.css';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Objeto de mapeo para mostrar nombres legibles en la tabla
const courseDisplayMap = {
  "10-4-A": "PKA",
  "10-4-B": "PKB",
  "10-5-A": "KA",
  "10-5-B": "KB",
  "110-1-A": "1A",
  "110-1-B": "1B",
  "110-2-A": "2A",
  "110-2-B": "2B",
  "110-3-A": "3A",
  "110-3-B": "3B",
  "110-4-A": "4A",
  "110-4-B": "4B",
  "110-5-A": "5A",
  "110-5-B": "5B",
  "110-6-A": "6A",
  "110-6-B": "6B",
  "110-7-A": "7A",
  "110-7-B": "7B",
  "110-8-A": "8A",
  "110-8-B": "8B",
  "310-1-A": "IA",
  "310-1-B": "IB",
  "310-2-A": "IIA",
  "310-2-B": "IIB",
  "310-3-A": "IIIA",
  "310-3-B": "IIIB",
  "310-4-A": "IVA",
  "310-4-B": "IVB"
};

const juniorCourses = [
  "10-4-A", "10-4-B", "10-5-A", "10-5-B", 
  "110-1-A", "110-1-B", "110-2-A", "110-2-B", "110-3-A", "110-3-B", "110-4-A", "110-4-B"
];

const seniorCourses = [
  "110-5-A", "110-5-B", "110-6-A", "110-6-B", 
  "110-7-A", "110-7-B", "110-8-A", "110-8-B", 
  "310-1-A", "310-1-B", "310-2-A", "310-2-B", "310-3-A", "310-3-B", "310-4-A", "310-4-B"
];

// Mapeo de número de mes a nombre
const monthNames = {
  3: "Marzo",
  4: "Abril",
  5: "Mayo",
  6: "Junio",
  7: "Julio",
  8: "Agosto",
  9: "Septiembre",
  10: "Octubre",
  11: "Noviembre",
  12: "Diciembre"
};

// Función para formatear números a 1 decimal
function formatAttendance(value) {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  return Number.isNaN(num) ? '-' : num.toFixed(1);
}

// Función para calcular el resumen de ausentismo crónico
function computeAusentismoSummary(data) {
  let reiterado = 0, cronicoGrave = 0, muyGrave = 0;
  data.forEach(student => {
    const avg = parseFloat(student.avg_attendance);
    if (!Number.isNaN(avg)) {
      if (avg >= 85 && avg < 90) {
        reiterado++;
      } else if (avg >= 50 && avg < 85) {
        cronicoGrave++;
      } else if (avg < 50) {
        muyGrave++;
      }
    }
  });
  const denominator = data.length;
  const totalPercent = denominator > 0 ? (((reiterado + cronicoGrave + muyGrave) / denominator) * 100).toFixed(0) + '%' : '-';
  return { reiterado, cronicoGrave, muyGrave, totalPercent, totalStudents: denominator };
}

export default function AusentismoCronico() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [sede, setSede] = useState('All'); // "All", "Junior", "Senior"
  const [comparisonMonth, setComparisonMonth] = useState(0);
  const [course, setCourse] = useState('All');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [students, setStudents] = useState([]);

  const doughnutChartRef = useRef(null);
  const barChartRef = useRef(null);

  // Estados para ordenamiento en la tabla de cursos y ranking
  const [sortConfigAggregated, setSortConfigAggregated] = useState({ key: null, direction: 'asc' });
  const [sortConfigRanking, setSortConfigRanking] = useState({ key: null, direction: 'asc' });

  // Función para obtener los datos de ausentismo usando el endpoint alternativo
  async function fetchAusentismoData() {
    try {
      setLoading(true);
      setError('');
      const params = { year, sede };
      if (comparisonMonth !== 0) {
        params.month = comparisonMonth;
      }
      // Usamos el endpoint alternativo "/dashboard/ausentismo_v2" que devuelve full_course
      const response = await axios.get('/dashboard/ausentismo_v2', {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      let data = response.data;
      // Si se selecciona un curso específico, filtramos por la propiedad full_course
      if (course !== 'All') {
        data = data.filter(student => student.full_course === course);
      }
      setStudents(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al obtener datos de ausentismo crónico');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAusentismoData();
  }, [year, sede, comparisonMonth, course]);

  const sedeLabel = sede === 'All' ? 'todas' : (sede === 'Junior' ? 'Sede Junior' : 'Sede Senior');
  const titleText = comparisonMonth === 0 
    ? `Ausentismo Crónico Anual (${sedeLabel})` 
    : `Ausentismo Crónico del mes de ${monthNames[comparisonMonth]} (${sedeLabel})`;

  const ausentismoSummary = useMemo(() => computeAusentismoSummary(students), [students]);

  const doughnutData = useMemo(() => {
    if (students.length === 0) return null;
    return {
      labels: ['Crónico Reiterado (85-89.9)', 'Crónico Grave (50-84.9)', 'Muy Grave (<50)'],
      datasets: [{
        data: [ausentismoSummary.reiterado, ausentismoSummary.cronicoGrave, ausentismoSummary.muyGrave],
        backgroundColor: ['#8e44ad', '#2980b9', '#e74c3c']
      }]
    };
  }, [students, ausentismoSummary]);

  const barData = useMemo(() => {
    if (students.length === 0) return null;
    const total = students.reduce((sum, s) => {
      const avg = parseFloat(s.avg_attendance);
      return !Number.isNaN(avg) ? sum + avg : sum;
    }, 0);
    const avgAttendance = students.length > 0 ? total / students.length : 0;
    const ausentismoAvg = (100 - avgAttendance).toFixed(1);
    return {
      labels: [titleText],
      datasets: [{
        label: 'Ausentismo (%)',
        data: [ausentismoAvg],
        backgroundColor: 'rgba(231, 76, 60, 0.6)'
      }]
    };
  }, [students, titleText]);

  const aggregatedCourses = useMemo(() => {
    if (course !== 'All') return [];
    const groups = {};
    students.forEach(student => {
      const key = student.full_course;
      if (!groups[key]) {
        groups[key] = { count: 0, sum: 0, reiterado: 0, cronicoGrave: 0, muyGrave: 0 };
      }
      groups[key].count++;
      const avg = parseFloat(student.avg_attendance);
      if (!Number.isNaN(avg)) {
        groups[key].sum += avg;
        if (avg >= 85 && avg < 90) groups[key].reiterado++;
        else if (avg >= 50 && avg < 85) groups[key].cronicoGrave++;
        else if (avg < 50) groups[key].muyGrave++;
      }
    });
    return Object.keys(groups).map(key => ({
      course: courseDisplayMap[key] || key,
      count: groups[key].count,
      average: (groups[key].sum / groups[key].count).toFixed(1),
      reiterado: groups[key].reiterado,
      cronicoGrave: groups[key].cronicoGrave,
      muyGrave: groups[key].muyGrave
    }));
  }, [students, course]);

  // Ranking de 200 estudiantes con menor asistencia
  const lowest200 = useMemo(() => {
    if (students.length === 0) return [];
    return [...students]
      .filter(s => s.avg_attendance !== null && s.avg_attendance !== undefined)
      .sort((a, b) => parseFloat(a.avg_attendance) - parseFloat(b.avg_attendance))
      .slice(0, 200);
  }, [students]);

  // Ordenamiento para la tabla de cursos (aggregated)
  const sortedAggregatedCourses = useMemo(() => {
    if (!sortConfigAggregated.key) return aggregatedCourses;
    return [...aggregatedCourses].sort((a, b) => {
      let aVal = a[sortConfigAggregated.key];
      let bVal = b[sortConfigAggregated.key];
      if (sortConfigAggregated.key === 'course') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        return sortConfigAggregated.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return sortConfigAggregated.direction === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      }
    });
  }, [aggregatedCourses, sortConfigAggregated]);

  const requestSortAggregated = (key) => {
    let direction = 'asc';
    if (sortConfigAggregated.key === key && sortConfigAggregated.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfigAggregated({ key, direction });
  };

  // Ordenamiento para la tabla de ranking
  const sortedRanking = useMemo(() => {
    if (!sortConfigRanking.key) return lowest200;
    return [...lowest200].sort((a, b) => {
      let aVal = a[sortConfigRanking.key];
      let bVal = b[sortConfigRanking.key];
      if (sortConfigRanking.key === 'nombre' || sortConfigRanking.key === 'rut' || sortConfigRanking.key === 'full_course') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        return sortConfigRanking.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else if (sortConfigRanking.key === 'avg_attendance') {
        return sortConfigRanking.direction === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      }
      return 0;
    });
  }, [lowest200, sortConfigRanking]);

  const requestSortRanking = (key) => {
    let direction = 'asc';
    if (sortConfigRanking.key === key && sortConfigRanking.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfigRanking({ key, direction });
  };

  // Opciones para los gráficos
  const barOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: titleText },
      datalabels: {
        formatter: (value) => value + '%',
        color: '#000'
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Distribución de Ausentismo Crónico' },
      datalabels: {
        formatter: (value, ctx) => {
          let sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
          let percentage = (value * 100 / sum).toFixed(1) + '%';
          return percentage;
        },
        color: '#fff'
      }
    }
  };

  const courseOptions = useMemo(() => {
    let coursesList = [];
    if (sede === 'Junior') {
      coursesList = juniorCourses;
    } else if (sede === 'Senior') {
      coursesList = seniorCourses;
    } else {
      coursesList = [...juniorCourses, ...seniorCourses];
    }
    return Array.from(new Set(coursesList));
  }, [sede]);

  // Función para exportar a Excel
  const exportWorkbook = async () => {
    const workbook = new ExcelJS.Workbook();

    // Hoja 1: Resumen General
    const summarySheet = workbook.addWorksheet('Resumen Ausentismo');
    summarySheet.addRow(['Año', year]);
    summarySheet.addRow(['Sede', sede]);
    summarySheet.addRow(['Mes', comparisonMonth === 0 ? 'Anual' : monthNames[comparisonMonth]]);
    summarySheet.addRow(['Curso', course === 'All' ? 'Todos' : (courseDisplayMap[course] || course)]);
    summarySheet.addRow(['Total Estudiantes', ausentismoSummary.totalStudents]);
    summarySheet.addRow(['Crónico Reiterado', ausentismoSummary.reiterado]);
    summarySheet.addRow(['Crónico Grave', ausentismoSummary.cronicoGrave]);
    summarySheet.addRow(['Muy Grave', ausentismoSummary.muyGrave]);
    summarySheet.addRow(['Total Ausentismo (%)', ausentismoSummary.totalPercent]);

    // Hoja 2: Agregación por Curso (solo si no se filtra por curso)
    if (course === 'All' && aggregatedCourses.length > 0) {
      const coursesSheet = workbook.addWorksheet('Por Curso');
      coursesSheet.addRow(['Curso', 'Cantidad de Estudiantes', 'Promedio (%)', 'Reiterado', 'Crónico Grave', 'Muy Grave']);
      sortedAggregatedCourses.forEach(item => {
        coursesSheet.addRow([item.course, item.count, item.average + '%', item.reiterado, item.cronicoGrave, item.muyGrave]);
      });
    }

    // Hoja 3: Ranking de 200 Estudiantes con Menor Asistencia
    if (lowest200.length > 0) {
      const rankingSheet = workbook.addWorksheet('Ranking 200');
      rankingSheet.addRow(['Nombre', 'RUT', 'Curso', 'Promedio (%)']);
      sortedRanking.forEach(student => {
        rankingSheet.addRow([
          student.nombre,
          student.rut,
          courseDisplayMap[student.full_course] || student.full_course,
          formatAttendance(student.avg_attendance) + '%'
        ]);
      });
    }

    // Hoja 4: Datos Raw (por estudiante)
    const dataSheet = workbook.addWorksheet('Datos');
    dataSheet.addRow(['Nombre', 'RUT', 'Curso', 'Promedio (%)']);
    students.forEach(student => {
      dataSheet.addRow([
        student.nombre,
        student.rut,
        courseDisplayMap[student.full_course] || student.full_course,
        formatAttendance(student.avg_attendance) + '%'
      ]);
    });

    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `AusentismoCronico_${year}.xlsx`);
  };

  // Estilo inline para el spinner
  const spinnerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  return (
    <div className="dashboard-container">
      <h1>Ausentismo Crónico</h1>
      
      {/* Filtros */}
      <div className="filters">
        <div className="filter">
          <label>Año:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            <option value={2026}>2026</option>
            <option value={2025}>2025</option>
            <option value={2024}>2024</option>
            <option value={2023}>2023</option>
            <option value={2022}>2022</option>
          </select>
        </div>
        <div className="filter">
          <label>Sede:</label>
          <select value={sede} onChange={e => setSede(e.target.value)}>
            <option value="All">Todas</option>
            <option value="Junior">Sede Junior</option>
            <option value="Senior">Sede Senior</option>
          </select>
        </div>
        <div className="filter">
          <label>Mes de comparación:</label>
          <select value={comparisonMonth} onChange={e => setComparisonMonth(Number(e.target.value))}>
            <option value={0}>Todos (Anual)</option>
            <option value={3}>Marzo</option>
            <option value={4}>Abril</option>
            <option value={5}>Mayo</option>
            <option value={6}>Junio</option>
            <option value={7}>Julio</option>
            <option value={8}>Agosto</option>
            <option value={9}>Septiembre</option>
            <option value={10}>Octubre</option>
            <option value={11}>Noviembre</option>
            <option value={12}>Diciembre</option>
          </select>
        </div>
        <div className="filter">
          <label>Curso:</label>
          <select value={course} onChange={e => setCourse(e.target.value)}>
            <option value="All">Todos</option>
            {courseOptions.map(c => (
              <option key={c} value={c}>{courseDisplayMap[c] || c}</option>
            ))}
          </select>
        </div>
      </div>
      
      {loading && (
        <div className="loading-container" style={spinnerStyle}>
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      
      {/* Contenedor en una fila con 3 columnas: resumen, gráfico de barras y gráfico de dona */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
         {/* Resumen General */}
         <div style={{ flex: '1', margin: '10px', padding: '15px', border: '1px solid #ecf0f1', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
           <h2>Resumen General</h2>
           <p><strong>Total Estudiantes Evaluados:</strong> {ausentismoSummary.totalStudents}</p>
           <p><strong>Crónico Reiterado (85-89.9):</strong> {ausentismoSummary.reiterado}</p>
           <p><strong>Crónico Grave (50-84.9):</strong> {ausentismoSummary.cronicoGrave}</p>
           <p><strong>Muy Grave (&lt;50):</strong> {ausentismoSummary.muyGrave}</p>
           <p><strong>Total Ausentismo:</strong> {ausentismoSummary.totalPercent}</p>
         </div>
         {/* Gráfico de Barras */}
         <div style={{ flex: '1', margin: '10px', maxWidth: '400px', height: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #ecf0f1', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
           {barData ? (
             <>
               <Bar ref={barChartRef} data={barData} options={barOptions} />
               <p style={{ fontSize: '0.8em', textAlign: 'center', marginTop: '5px' }}>
                 Nota: Este gráfico muestra el ausentismo global, calculado como 100% menos el promedio de asistencia de los alumnos.
               </p>
             </>
           ) : (
             <p>No hay datos para mostrar.</p>
           )}
         </div>
         {/* Gráfico de Dona */}
         <div style={{ flex: '1', margin: '10px', maxWidth: '400px', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ecf0f1', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
           {doughnutData ? (
             <Doughnut ref={doughnutChartRef} data={doughnutData} options={doughnutOptions} />
           ) : (
             <p>No hay datos para mostrar.</p>
           )}
         </div>
      </div>

      {/* Agregación por Curso (si no se filtra por curso) */}
      {course === 'All' && aggregatedCourses.length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <h2>Ausentismo por Curso</h2>
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => requestSortAggregated('course')}>Curso</th>
                <th onClick={() => requestSortAggregated('count')}>Cantidad de Estudiantes</th>
                <th onClick={() => requestSortAggregated('average')}>Promedio (%)</th>
                <th onClick={() => requestSortAggregated('reiterado')}>Crónico Reiterado</th>
                <th onClick={() => requestSortAggregated('cronicoGrave')}>Crónico Grave</th>
                <th onClick={() => requestSortAggregated('muyGrave')}>Muy Grave</th>
              </tr>
            </thead>
            <tbody>
              {sortedAggregatedCourses.map((item, index) => (
                <tr key={index}>
                  <td>{item.course}</td>
                  <td>{item.count}</td>
                  <td>{item.average}%</td>
                  <td>{item.reiterado}</td>
                  <td>{item.cronicoGrave}</td>
                  <td>{item.muyGrave}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tabla: Ranking de 200 Estudiantes con Menor Asistencia */}
      {lowest200.length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <h2>Ranking: 200 Estudiantes con Menor Asistencia</h2>
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => requestSortRanking('nombre')}>Nombre</th>
                <th onClick={() => requestSortRanking('rut')}>RUT</th>
                <th onClick={() => requestSortRanking('full_course')}>Curso</th>
                <th onClick={() => requestSortRanking('avg_attendance')}>Promedio (%)</th>
              </tr>
            </thead>
            <tbody>
              {sortedRanking.map((student, index) => (
                <tr key={index}>
                  <td>{student.nombre}</td>
                  <td>{student.rut}</td>
                  <td>{courseDisplayMap[student.full_course] || student.full_course}</td>
                  <td>{formatAttendance(student.avg_attendance)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Exportación a Excel */}
      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <button className="create-button" onClick={exportWorkbook}>Exportar a Excel</button>
      </div>
    </div>
  );
}
